import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { QuotationVersion } from '@/components/quotation/PublishButton';

// Interface para representar uma cotação
interface Quotation {
  id: string;
  is_published: boolean;
  published_id: string | null;
  original_id: string | null;
  version: 'work' | 'published' | 'history';
  published_at: string | null;
  [key: string]: any; // Para outros campos da cotação
}

/**
 * Serviço para gerenciar o versionamento de cotações
 */
export const quotationVersioningService = {
  /**
   * Salva uma versão de trabalho (work) de uma cotação
   * @param quotationData Dados da cotação
   * @returns A cotação salva
   */
  async saveWorkVersion(quotationData: any): Promise<Quotation | null> {
    try {
      // Se a cotação não tem ID, é uma nova cotação
      if (!quotationData.id) {
        quotationData.id = uuidv4();
        quotationData.version = 'work';
        
        const { data, error } = await supabase
          .from('quotations')
          .insert(quotationData)
          .select()
          .single();
          
        if (error) throw error;
        return data;
      }
      
      // Caso contrário, atualiza a cotação existente
      // Certifique-se de que a versão está definida como 'work'
      quotationData.version = 'work';
      
      const { data, error } = await supabase
        .from('quotations')
        .update({
          ...quotationData,
          updated_at: new Date().toISOString()
        })
        .eq('id', quotationData.id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  },
  
  /**
   * Publica uma versão da cotação
   * @param quotationId ID da cotação de trabalho (work) a ser publicada
   * @returns A cotação publicada
   */
  async publishQuotation(quotationId: string): Promise<Quotation | null> {
    try {
      // 1. Buscar a cotação de trabalho
      const { data: workQuotations, error: fetchError } = await supabase
        .from('quotations')
        .select('*')
        .eq('id', quotationId);
        
      if (fetchError) {
        throw fetchError;
      }
      
      if (!workQuotations || workQuotations.length === 0) {
        throw new Error('Cotação não encontrada');
      }
      
      const workQuotation = workQuotations[0];
      
      // Garantir que a versão é 'work'
      await supabase
        .from('quotations')
        .update({ version: 'work' })
        .eq('id', quotationId);
      
      // 2. Verificar se já existe uma versão publicada para esta cotação
      const { data: publishedQuotations, error: publishedError } = await supabase
        .from('quotations')
        .select('*')
        .eq('original_id', quotationId)
        .eq('version', 'published');
      
      if (publishedError) {
        // Erro tratado silenciosamente
      }
      
      const existingPublished = publishedQuotations && publishedQuotations.length > 0 
        ? publishedQuotations[0] 
        : null;
      
      // 3. Se já existe uma versão publicada, mova-a para o histórico
      if (existingPublished) {
        // 3.1 Verificar se já existe uma versão histórica
        const { data: historyQuotations, error: historyError } = await supabase
          .from('quotations')
          .select('*')
          .eq('original_id', quotationId)
          .eq('version', 'history');
          
        if (historyError) {
          // Erro tratado silenciosamente
        }
        
        const existingHistory = historyQuotations && historyQuotations.length > 0
          ? historyQuotations[0]
          : null;
          
        // 3.2 Se existir uma versão histórica, exclua-a
        if (existingHistory) {
          const { error: deleteError } = await supabase
            .from('quotations')
            .delete()
            .eq('id', existingHistory.id);
        }
        
        // 3.3 Mover a versão publicada atual para o histórico
        const { error: updateError } = await supabase
          .from('quotations')
          .update({
            version: 'history',
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPublished.id);
      }
      
      // 4. Criar uma nova versão publicada
      const publishedId = existingPublished?.published_id || uuidv4();
      
      // Preparar os dados para publicação - remover campos que não devem ser duplicados
      const publishedData = {
        ...workQuotation,
        id: uuidv4(), // Novo ID para o registro
        original_id: quotationId, // Referência à cotação original
        published_id: publishedId, // ID consistente para links
        is_published: true,
        version: 'published',
        published_at: new Date().toISOString()
      };
      
      // Remover campos que não devem ser duplicados
      delete publishedData.created_at;
      delete publishedData.updated_at;
      
      const { data: publishedQuotation, error: publishError } = await supabase
        .from('quotations')
        .insert(publishedData)
        .select()
        .single();
        
      if (publishError) {
        throw publishError;
      }
      
      // 5. Atualizar a cotação original para referenciar o published_id
      const { error: updateOriginalError } = await supabase
        .from('quotations')
        .update({
          published_id: publishedId,
          updated_at: new Date().toISOString()
        })
        .eq('id', quotationId);
      
      return publishedQuotation;
    } catch (error) {
      return null;
    }
  },
  
  /**
   * Republicar uma versão histórica
   * @param historyQuotationId ID da cotação histórica
   * @returns A cotação republicada
   */
  async republishHistoryVersion(historyQuotationId: string): Promise<Quotation | null> {
    try {
      // 1. Buscar a cotação histórica
      const { data: historyQuotations, error: fetchError } = await supabase
        .from('quotations')
        .select('*')
        .eq('id', historyQuotationId);
        
      if (fetchError) {
        throw fetchError;
      }
      
      if (!historyQuotations || historyQuotations.length === 0) {
        throw new Error('Versão histórica não encontrada');
      }
      
      const historyQuotation = historyQuotations[0];
      
      if (historyQuotation.version !== 'history') {
        throw new Error('A cotação selecionada não é uma versão histórica');
      }
      
      // 2. Buscar a versão publicada atual (que será movida para o histórico)
      const { data: publishedQuotations, error: publishedError } = await supabase
        .from('quotations')
        .select('*')
        .eq('original_id', historyQuotation.original_id)
        .eq('version', 'published');
        
      if (publishedError) {
        throw publishedError;
      }
      
      if (!publishedQuotations || publishedQuotations.length === 0) {
        throw new Error('Versão publicada atual não encontrada');
      }
      
      const currentPublished = publishedQuotations[0];
      
      // 3. Mover a versão publicada atual para o histórico
      const { error: updatePublishedError } = await supabase
        .from('quotations')
        .update({
          version: 'history',
          updated_at: new Date().toISOString()
        })
        .eq('id', currentPublished.id);
        
      if (updatePublishedError) {
        throw updatePublishedError;
      }
      
      // 4. Promover a versão histórica para publicada
      const { error: updateHistoryError } = await supabase
        .from('quotations')
        .update({
          version: 'published',
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', historyQuotationId);
        
      if (updateHistoryError) {
        throw updateHistoryError;
      }
      
      // 5. Buscar a cotação republicada
      const { data: republicedQuotations, error } = await supabase
        .from('quotations')
        .select('*')
        .eq('id', historyQuotationId);
        
      if (error || !republicedQuotations || republicedQuotations.length === 0) {
        throw new Error('Não foi possível recuperar a cotação republicada');
      }
      
      const republicedQuotation = republicedQuotations[0];
      
      return republicedQuotation;
    } catch (error) {
      return null;
    }
  },
  
  /**
   * Obter todas as versões de uma cotação
   * @param originalQuotationId ID da cotação original (versão work)
   * @returns As versões da cotação
   */
  async getQuotationVersions(originalQuotationId: string): Promise<{
    workVersion: QuotationVersion | null;
    publishedVersion: QuotationVersion | null;
    historyVersion: QuotationVersion | null;
  }> {
    try {
      // Buscar a versão de trabalho
      const { data: workQuotations, error: workError } = await supabase
        .from('quotations')
        .select('*')
        .eq('id', originalQuotationId)
        .eq('version', 'work');
        
      if (workError) {
        throw workError;
      }
      
      const workQuotation = workQuotations && workQuotations.length > 0
        ? workQuotations[0]
        : null;
      
      // Buscar as versões relacionadas
      const { data: relatedVersions, error: relatedError } = await supabase
        .from('quotations')
        .select('*')
        .eq('original_id', originalQuotationId);
        
      if (relatedError) {
        throw relatedError;
      }
      
      // Filtrar as versões
      const publishedVersion = relatedVersions?.find(v => v.version === 'published') || null;
      const historyVersion = relatedVersions?.find(v => v.version === 'history') || null;
      
      return {
        workVersion: workQuotation as QuotationVersion,
        publishedVersion: publishedVersion as QuotationVersion,
        historyVersion: historyVersion as QuotationVersion
      };
    } catch (error) {
      return {
        workVersion: null,
        publishedVersion: null,
        historyVersion: null
      };
    }
  }
}; 